import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useAutocomplete, useInput, useSelect, useViewport } from '@chilecompra/react-kit'
import { Autocomplete, Button, Input, Select, Typography } from '@chilecompra/react-kit/components'
import { formatDNI } from '@chilecompra/react-kit/utils'
import { PROFILES, SEARCH_TYPES } from '../../config/settings/constants'
import { onSearchBuyersThunk, onSearchBuyersSuccess } from './SearchBuyerSection.actions'
import {
  Card,
  Content,
  Form,
  GridContainer,
  GridItem,
  HeaderText,
  Link,
  Root,
  SectionHeaderContainer
} from './SearchBuyerSection.styles'
import { REACT_APP_MP_URL } from '../../config/settings/environment'

/**
 * The SearchBuyerSection's container.
 */
const SearchBuyerSection = () => {
  const dispatch = useDispatch()
  const { loading, options } = useSelector(state => state.searchBuyerSection)
  const [selectedValue, setSelectedValue] = useState(null)
  const { size } = useViewport()
  const { value: typeValue, onChange: handleChangeType } = useSelect({
    initialValue: 2,
    changeCallback: updatedValue => {
      if (updatedValue === 2) {
        setTypeValue(2)
      }
      resetFields()
    }
  })
  const { option, value, onChange, onSelect } = useAutocomplete({
    changeCallback: updatedValue => {
      if (updatedValue.length < 3) {
        return
      }
      dispatch(onSearchBuyersThunk(updatedValue, typeValue))
    },
    selectCallback: updatedValue => {
      if (options.length < 1) {
        return
      }
      setSelectedValue(updatedValue?.value)
    }
  })

  const {
    value: searchTypeValue,
    onChange: handleSearchTypeChange,
    setValue: setTypeValue
  } = useSelect({
    initialValue: 2,
    changeCallback: () => {
      resetFields()
    }
  })
  const resetFields = () => {
    onChange('')
    handleDniChange('')
    dispatch(onSearchBuyersSuccess({ options: [] }))
  }
  const {
    error: dniError,
    value: dniValue,
    onChange: handleDniChange
  } = useInput({
    changeCallback: updatedValue => {
      if (updatedValue.length < 4) {
        return
      }
      dispatch(onSearchBuyersThunk(updatedValue, typeValue))
    },
    formatCallbacks: [formatDNI]
  })

  const handleSubmit = useCallback(
    event => {
      event.preventDefault()

      if ((!selectedValue && !dniValue) || options.length < 1) {
        return
      }
      window.location.href =
        typeValue === 2
          ? `/ficha-organismo/${selectedValue}`
          : (window.location.href = `/organismos-proveedores/${searchTypeValue === 1 ? dniValue : selectedValue}`)
    },
    [options, selectedValue, typeValue, dniValue]
  )
  const mdValue = useMemo(() => {
    if (size.isUpToMedium && typeValue === 1) return '985px'
    if (size.isUpToMedium && typeValue === 1 && searchTypeValue === 1) return '1025px'
    if (size.isUpToMedium) return '825px'
    return '100%'
  }, [typeValue])

  useEffect(() => {
    onChange('')
  }, [typeValue])
  return (
    <Root>
      <SectionHeaderContainer height={size.isUpToMedium ? '480px' : 'auto'}>
        <Content>
          <HeaderText margin={size.isUpToMedium ? '96px 0 0' : '48px 0 0'}>
            Ficha organismo comprador y proveedor
          </HeaderText>
          <Typography
            color="white"
            fontSize="34px"
            fontWeight="bold"
            lineHeight="38px"
            margin={size.isUpToMedium ? '16px 0 0' : '24px 0'}
            variant="h2"
            width={size.isUpToMedium ? '412px' : undefined}
          >
            Información de cada comprador y proveedor del Estado
          </Typography>
          <GridContainer>
            <GridItem xs={size.isUpToMedium ? 5 : 12}>
              <Typography
                color="white"
                fontSize="18px"
                lineHeight="24px"
                margin="16px 0 0"
                variant="body1"
                width={size.isUpToMedium ? '448px' : undefined}
              >
                Ingresa el nombre del organismo comprador o proveedor que deseas consultar y accede a su ficha donde
                encontrarás el detalle de sus montos transados, mecanismos de compra utilizados, licitaciones,
                proveedores y más.
              </Typography>
            </GridItem>
            <GridItem xs={size.isUpToMedium ? 7 : 12}>
              <Typography
                color="white"
                fontSize="18px"
                lineHeight="24px"
                margin="16px 0 0"
                variant="body1"
                width={size.isUpToMedium ? '448px' : undefined}
              >
                Toda la información de los organismos compradores se obtiene desde{' '}
                <Link href={REACT_APP_MP_URL}>Mercado Público</Link> en base a las órdenes de compra generadas en la
                plataforma.
              </Typography>
            </GridItem>
          </GridContainer>
          <GridContainer justifyContent="center">
            <Card
              color="blank"
              margin={size.isUpToMedium ? '92px 0 0' : '48px 0 24px'}
              padding={size.isUpToMedium ? '0 32px' : '32px'}
              width={mdValue}
            >
              <Form onSubmit={handleSubmit}>
                <GridContainer alignItems="center" justifyContent="space-between" spacing={2}>
                  <GridItem md={3} xs={12}>
                    <Select
                      label={size.isUpToMedium ? ' ' : 'Tipo'}
                      options={PROFILES}
                      value={typeValue}
                      onChange={handleChangeType}
                    />
                  </GridItem>
                  {typeValue === 1 && (
                    <GridItem md={2} xs={12}>
                      <Select
                        label={size.isUpToMedium ? ' ' : 'Identificador'}
                        options={SEARCH_TYPES}
                        value={searchTypeValue}
                        onChange={handleSearchTypeChange}
                      />
                    </GridItem>
                  )}

                  {typeValue === 1 && searchTypeValue === 1 && (
                    <GridItem flexGrow={1} md={4} xs={12}>
                      <Input
                        error={dniError}
                        label={size.isUpToMedium ? ' ' : 'Rut chileno'}
                        value={dniValue}
                        onChange={handleDniChange}
                        placeholder="Ingresa el rut del proveedor"
                      />
                    </GridItem>
                  )}

                  {searchTypeValue !== 1 && (
                    <GridItem md={typeValue === 1 ? 4 : 6} xs={12}>
                      <Autocomplete
                        label={size.isUpToMedium ? ' ' : 'nombre'}
                        option={option}
                        options={options}
                        placeholder={'Ingresa el nombre del ' + (typeValue === 2 ? 'organismo' : 'proveedor')}
                        value={value}
                        waiting={loading}
                        onChange={onChange}
                        onSelect={onSelect}
                      />
                    </GridItem>
                  )}
                  <GridItem md={3} xs={12} justifyContent="center">
                    <Button color="primary" type="submit">
                      Ver ficha
                    </Button>
                  </GridItem>
                </GridContainer>
              </Form>
            </Card>
          </GridContainer>
        </Content>
      </SectionHeaderContainer>
    </Root>
  )
}

export default SearchBuyerSection
